import type { BrandBreakdown, BrandSegment } from '../types';
import { formatBrl } from '../utils';

type Props = {
    data: BrandBreakdown;
};

type DonutMode = 'qty' | 'amount';

function compactBrl(value: number): string {
    if (value >= 1_000_000) {
        return `R$ ${(value / 1_000_000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}M`;
    }
    if (value >= 10_000) {
        return `R$ ${Math.round(value / 1_000).toLocaleString('pt-BR')}k`;
    }
    if (value >= 1_000) {
        return `R$ ${(value / 1_000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}k`;
    }
    return formatBrl(value);
}

function sliceLabel(segment: BrandSegment, mode: DonutMode): string {
    if (mode === 'amount') {
        return compactBrl(segment.amount);
    }
    return Math.round(segment.qty).toLocaleString('pt-BR');
}

function slicePercent(segment: BrandSegment, mode: DonutMode): number {
    return mode === 'amount' ? segment.amount_percent : segment.percent;
}

function BrandDonut({
    segments,
    mode,
    centerValue,
    centerSub,
    centerHint,
}: {
    segments: Array<BrandSegment & { dash: number; offset: number; labelX: number; labelY: number }>;
    mode: DonutMode;
    centerValue: string;
    centerSub: string;
    centerHint?: string;
}) {
    const radius = 46;
    const circumference = 2 * Math.PI * radius;

    return (
        <div className="flex flex-col items-center gap-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
                {mode === 'qty' ? 'Por quantidade' : 'Por valor'}
            </p>
            <div className="relative mx-auto h-36 w-36 sm:h-40 sm:w-40 lg:h-36 lg:w-36 2xl:h-40 2xl:w-40">
                <svg viewBox="0 0 120 120" className="h-full w-full">
                    <circle
                        cx="60"
                        cy="60"
                        r={radius}
                        fill="none"
                        className="stroke-gray-200 dark:stroke-zinc-700"
                        strokeWidth="18"
                        transform="rotate(-90 60 60)"
                    />
                    {segments.map((s) => (
                        <circle
                            key={`${mode}-${s.brand}`}
                            cx="60"
                            cy="60"
                            r={radius}
                            fill="none"
                            stroke={s.color}
                            strokeWidth="18"
                            strokeDasharray={`${s.dash} ${circumference}`}
                            strokeDashoffset={-s.offset}
                            transform="rotate(-90 60 60)"
                        />
                    ))}
                    {segments.map((s) => (
                        slicePercent(s, mode) >= 8 ? (
                            <g key={`${mode}-${s.brand}-label`}>
                                <circle cx={s.labelX} cy={s.labelY} r="5.4" fill={s.color} stroke="#fff" strokeWidth="0.8" />
                                <text
                                    x={s.labelX}
                                    y={s.labelY + 1.2}
                                    textAnchor="middle"
                                    fontSize="3"
                                    fontWeight="700"
                                    fill="#fff"
                                >
                                    {sliceLabel(s, mode)}
                                </text>
                            </g>
                        ) : null
                    ))}
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center px-2 text-center">
                    <span className="text-lg font-bold leading-tight text-gray-900 sm:text-xl dark:text-zinc-100">{centerValue}</span>
                    <span className="text-[10px] text-gray-500 dark:text-zinc-400">{centerSub}</span>
                    {centerHint && <span className="mt-0.5 text-[10px] text-gray-400 dark:text-zinc-500">{centerHint}</span>}
                </div>
            </div>
        </div>
    );
}

function buildSegments(segments: BrandSegment[], mode: DonutMode) {
    const radius = 46;
    const circumference = 2 * Math.PI * radius;
    let offset = 0;
    let angleCursor = 0;

    return segments.map((entry) => {
        const percent = slicePercent(entry, mode);
        const dash = (percent / 100) * circumference;
        const middlePercent = angleCursor + percent / 2;
        const angleDeg = middlePercent * 3.6 - 90;
        const labelRadius = 52;
        const segment = {
            ...entry,
            dash,
            offset,
            labelX: 60 + Math.cos((angleDeg * Math.PI) / 180) * labelRadius,
            labelY: 60 + Math.sin((angleDeg * Math.PI) / 180) * labelRadius,
        };
        offset += dash;
        angleCursor += percent;
        return segment;
    });
}

export default function PloomesDealBrandChart({ data }: Props) {
    if (!data.segments.length) {
        return (
            <p className="text-sm text-gray-500 dark:text-zinc-400">
                Não foi possível identificar marcas nos produtos deste pedido.
            </p>
        );
    }

    const qtySegments = buildSegments(data.segments, 'qty');
    const amountSegments = buildSegments(data.segments, 'amount');

    return (
        <div className="space-y-4">
            {/*
              Mobile (coluna full-width): donuts lado a lado.
              Desktop (sidebar estreita): empilhados para caber sem espremer os produtos.
            */}
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-1 lg:gap-4">
                <BrandDonut
                    segments={qtySegments}
                    mode="qty"
                    centerValue={Math.round(data.total_qty).toLocaleString('pt-BR')}
                    centerSub="unidades"
                    centerHint={`${data.total_lines} SKUs`}
                />
                <BrandDonut
                    segments={amountSegments}
                    mode="amount"
                    centerValue={compactBrl(data.total_amount)}
                    centerSub="valor total"
                    centerHint={`${data.total_lines} SKUs`}
                />
            </div>

            <div className="space-y-1.5 border-t border-gray-100 pt-3 dark:border-zinc-800">
                {data.segments.map((item) => (
                    <div key={item.brand} className="flex items-center justify-between gap-2 text-xs">
                        <div className="flex min-w-0 items-center gap-2">
                            <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: item.color }} />
                            <span className="truncate font-medium text-gray-700 dark:text-zinc-200">{item.brand}</span>
                        </div>
                        <div className="flex flex-col items-end whitespace-nowrap text-right">
                            <span className="font-semibold text-gray-800 dark:text-zinc-100">
                                {Math.round(item.qty).toLocaleString('pt-BR')} un. ({item.percent.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%)
                            </span>
                            <span className="text-[11px] font-semibold text-emerald-700 dark:text-emerald-400">
                                {formatBrl(item.amount)} ({item.amount_percent.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%)
                            </span>
                            <span className="text-[10px] text-gray-400 dark:text-zinc-500">{item.lines} SKUs</span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
